import { faChartArea, faChartPie } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { useMemo } from 'react';
import cn from 'classnames';
import { formatKyivDate } from 'helpers/formatKyivDate';
import { MoneyAmount } from 'components/MoneyAmount';
import styles from './Summary.scss';

const COLOR_PALETTE = [
  '#384bc7',
  '#8b5cf6',
  '#06b6d4',
  '#f59e0b',
  '#ec4899',
  '#10b981',
  '#ef4444',
  '#6366f1',
  '#14b8a6',
  '#f97316',
  '#a855f7',
  '#3b82f6',
  '#22c55e',
  '#eab308',
  '#84cc16',
  '#06b6d4',
  '#8b5cf6',
  '#d946ef',
  '#0ea5e9',
  '#64748b',
];

const CASH_CHART_COLORS = {
  income: '#10b981',
  expense: '#ef4444',
};

const EMPTY_CASH_TOTALS = { income: 0, expense: 0 };

const getColorForMethod = (index) => {
  const shuffledIndices = COLOR_PALETTE.map((_, i) => i).sort((a, b) => {
    const hashA = (a * 2654435761) % COLOR_PALETTE.length;
    const hashB = (b * 2654435761) % COLOR_PALETTE.length;
    return hashA - hashB;
  });

  return COLOR_PALETTE[shuffledIndices[index % COLOR_PALETTE.length]];
};

const formatChartCenterValue = (value) => {
  const absValue = Math.abs(value);
  if (absValue >= 1000) return `${value >= 0 ? '+' : ''}${(value / 1000).toFixed(1)}k`;
  return `${value >= 0 ? '+' : ''}${value}`;
};

export const Summary = ({ transactions, methods, searchDate, isLoading }) => {
  const cashTotals = useMemo(() => {
    if (!transactions?.length) return EMPTY_CASH_TOTALS;

    return transactions.reduce(
      (acc, { method, type, amount }) => {
        if (method?.value !== 'cash') return acc;
        if (type === 'income') acc.income += Number(amount);
        else if (type === 'expense') acc.expense += Number(amount);
        return acc;
      },
      { ...EMPTY_CASH_TOTALS }
    );
  }, [transactions]);

  const cashlessMethods = useMemo(() => methods?.filter((m) => m.value !== 'cash') || [], [methods]);

  const cashlessTotals = useMemo(() => {
    if (!transactions?.length || !cashlessMethods.length) return {};

    const initialTotals = Object.fromEntries(cashlessMethods.map((m) => [m.value, 0]));

    return transactions.reduce((acc, { method, type, amount, relatedCashboxID }) => {
      if (method?.value === 'cash' || relatedCashboxID) return acc;

      const sign = type === 'expense' || type === 'transfer_out' ? -1 : 1;
      const methodValue = method?.value;

      if (methodValue && methodValue in acc) {
        acc[methodValue] += sign * Number(amount);
      }

      return acc;
    }, initialTotals);
  }, [transactions, cashlessMethods]);

  const totalCashless = useMemo(
    () => Object.values(cashlessTotals).reduce((sum, amount) => sum + amount, 0),
    [cashlessTotals]
  );

  const totalCash = cashTotals.income - cashTotals.expense;

  const cashData = useMemo(
    () => [
      { name: 'Надходження', value: cashTotals.income, color: CASH_CHART_COLORS.income },
      { name: 'Списання', value: cashTotals.expense, color: CASH_CHART_COLORS.expense },
    ],
    [cashTotals]
  );

  const cashlessData = useMemo(
    () =>
      cashlessMethods
        .map((method, index) => ({
          name: method.label,
          value: Math.max(0, cashlessTotals[method.value] || 0),
          color: getColorForMethod(index),
        }))
        .filter((item) => item.value > 0),
    [cashlessMethods, cashlessTotals]
  );

  const isCashEmpty = cashTotals.income === 0 && cashTotals.expense === 0;
  const isCashlessEmpty = totalCashless === 0;

  const isToday = searchDate === formatKyivDate(new Date());

  const getEmptyCashText = () =>
    isToday ? 'Тиша в касі... чекаємо перші купюри 💵' : 'Готівки за обрану дату не було 😴';

  const getEmptyCashlessText = () =>
    isToday ? 'Поки що тихо... чекаємо перші перекази 💸' : 'Безготівкових платежів за обрану дату не було 😴';

  return (
    <>
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>Готівка</h3>
        {isLoading ? (
          <div className={styles.loaderWrapper}>
            <div className={styles.loader}></div>
          </div>
        ) : isCashEmpty ? (
          <div className={styles.emptyStateSmall}>
            <FontAwesomeIcon icon={faChartPie} className={styles.emptyStateIconSmall} />
            <p className={styles.emptyStateTextSmall}>{getEmptyCashText()}</p>
          </div>
        ) : (
          <div className={styles.chartContainer}>
            <div className={styles.pieChartWrapper}>
              <ResponsiveContainer width='100%' height={96}>
                <PieChart>
                  <Pie
                    data={cashData}
                    cx='50%'
                    cy='50%'
                    innerRadius={35}
                    outerRadius={45}
                    paddingAngle={2}
                    dataKey='value'
                  >
                    {cashData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className={styles.pieChartCenter}>
                <div className={styles.pieChartLabel}>Всього</div>
                <div className={styles.pieChartValue}>{formatChartCenterValue(totalCash)}</div>
              </div>
            </div>

            <div className={styles.chartDetails}>
              <div className={styles.chartDetailItem}>
                <div className={cn(styles.chartLegendDot, styles.green)} />
                <span className={styles.chartLabel}>Надходження</span>
                <MoneyAmount className={styles.chartValue} value={cashTotals.income} />
              </div>
              <div className={styles.chartDetailItem}>
                <div className={cn(styles.chartLegendDot, styles.red)} />
                <span className={styles.chartLabel}>Списання</span>
                <MoneyAmount className={styles.chartValue} value={cashTotals.expense} />
              </div>
              <div className={styles.chartTotal}>
                <span className={styles.chartTotalLabel}>Всього</span>
                <MoneyAmount
                  className={cn(styles.chartTotalAmount, totalCash >= 0 ? styles.positive : styles.negative)}
                  value={totalCash}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.card}>
        <h3 className={styles.cardTitle}>Безготівка</h3>
        {isLoading ? (
          <div className={styles.loaderWrapper}>
            <div className={styles.loader}></div>
          </div>
        ) : isCashlessEmpty ? (
          <div className={styles.emptyStateSmall}>
            <FontAwesomeIcon icon={faChartArea} className={styles.emptyStateIconSmall} />
            <p className={styles.emptyStateTextSmall}>{getEmptyCashlessText()}</p>
          </div>
        ) : (
          <div className={styles.chartContainer}>
            <div className={styles.pieChartWrapper}>
              <ResponsiveContainer width='100%' height={96}>
                <PieChart>
                  <Pie
                    data={cashlessData}
                    cx='50%'
                    cy='50%'
                    innerRadius={35}
                    outerRadius={45}
                    paddingAngle={2}
                    dataKey='value'
                  >
                    {cashlessData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className={styles.pieChartCenter}>
                <div className={styles.pieChartLabel}>Всього</div>
                <div className={styles.pieChartValue}>
                  {totalCashless >= 1000 ? `${(totalCashless / 1000).toFixed(1)}k` : totalCashless}
                </div>
              </div>
            </div>

            <div className={styles.chartDetails}>
              {cashlessMethods.map((method, index) => (
                <div key={method.value} className={styles.chartDetailItem}>
                  <div className={styles.chartLegendDot} style={{ backgroundColor: getColorForMethod(index) }} />
                  <span className={styles.chartLabel}>{method.label}</span>
                  <MoneyAmount className={styles.chartValue} value={cashlessTotals[method.value] || 0} />
                </div>
              ))}

              <div className={styles.chartTotal}>
                <span className={styles.chartTotalLabel}>Всього</span>
                <MoneyAmount className={cn(styles.chartTotalAmount, styles.success)} value={totalCashless} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
